import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { Close, Save } from '@mui/icons-material';
import useAuth from '../hooks/useAuth';
import axiosClient from '../api/axiosClient';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

interface UserProfile {
  id: number;
  username: string;
  email: string;
  role: string;
  nextcloudUsername?: string;
  nextcloudPassword?: string;
  nextcloudFolder?: string;
  nifColumn?: string;
  linkExpirationDays?: number;
  nameColumn?: string;
  emailColumn?: string;
  phoneColumn?: string;
  preferredLanguage?: 'en' | 'es' | 'de' | 'fr';
  createdAt: string;
  updatedAt: string;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Cargar datos del perfil cuando se abre el modal
  useEffect(() => {
    if (open && user) {
      loadProfile();
    }
  }, [open, user]);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosClient.get('/users/me');
      setProfile(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('profile.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await axiosClient.put('/users/me', {
        username: profile.username,
        email: profile.email,
        nextcloudUsername: profile.nextcloudUsername,
        nextcloudPassword: profile.nextcloudPassword,
        nextcloudFolder: profile.nextcloudFolder,
        nifColumn: profile.nifColumn,
        linkExpirationDays: profile.linkExpirationDays,
        nameColumn: profile.nameColumn,
        emailColumn: profile.emailColumn,
        phoneColumn: profile.phoneColumn,
        preferredLanguage: profile.preferredLanguage,
      });

      setSuccess(t('profile.success'));
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('profile.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof UserProfile, value: string | number) => {
    if (profile) {
      setProfile({ ...profile, [field]: value });
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          {t('profile.title')}
        </Typography>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : profile ? (
          <Box sx={{ pt: 1 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Información básica */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                  {t('profile.basicInfo')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('profile.username')}
                    value={profile.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.email')}
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.role')}
                    value={profile.role}
                    disabled
                    variant="outlined"
                    helperText={t('profile.roleHelp')}
                  />
                  <TextField
                    select
                    fullWidth
                    label={t('profile.preferredLanguage')}
                    value={profile.preferredLanguage || 'en'}
                    onChange={(e) => handleInputChange('preferredLanguage', e.target.value)}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="de">Deutsch</MenuItem>
                    <MenuItem value="fr">Français</MenuItem>
                  </TextField>
                </Box>
              </Box>

              {/* Configuración de Nextcloud */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                  {t('profile.nextcloud')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('profile.nextcloudUser')}
                    value={profile.nextcloudUsername || ''}
                    onChange={(e) => handleInputChange('nextcloudUsername', e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.nextcloudPassword')}
                    type="password"
                    value={profile.nextcloudPassword || ''}
                    onChange={(e) => handleInputChange('nextcloudPassword', e.target.value)}
                    variant="outlined"
                    helperText=""
                  />
                  <TextField
                    fullWidth
                    label={t('profile.nextcloudFolder')}
                    value={profile.nextcloudFolder || ''}
                    onChange={(e) => handleInputChange('nextcloudFolder', e.target.value)}
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Configuración de columnas */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                  {t('profile.columns')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('profile.nifColumn')}
                    value={profile.nifColumn || ''}
                    onChange={(e) => handleInputChange('nifColumn', e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.nameColumn')}
                    value={profile.nameColumn || ''}
                    onChange={(e) => handleInputChange('nameColumn', e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.emailColumn')}
                    value={profile.emailColumn || ''}
                    onChange={(e) => handleInputChange('emailColumn', e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.phoneColumn')}
                    value={profile.phoneColumn || ''}
                    onChange={(e) => handleInputChange('phoneColumn', e.target.value)}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.linkExpirationDays')}
                    type="number"
                    value={profile.linkExpirationDays || 0}
                    onChange={(e) => handleInputChange('linkExpirationDays', parseInt(e.target.value) || 0)}
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Información del sistema */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                  {t('profile.systemInfo')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('profile.createdAt')}
                    value={new Date(profile.createdAt).toLocaleString('es-ES')}
                    disabled
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label={t('profile.updatedAt')}
                    value={new Date(profile.updatedAt).toLocaleString('es-ES')}
                    disabled
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          disabled={saving || !profile}
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileModal;
